import { useEffect, useState } from 'react'

/**
 * Watches a scroll container and reports which way the user is heading.
 * Downward travel has to accumulate past `threshold` px before 'down'
 * fires (taps and micro-jitter shouldn't hide the mobile ribbons);
 * meaningful upward travel — or landing back at the very top — flips
 * straight to 'up'. Pass the element itself (not a ref object) so the
 * listener re-attaches when the container mounts or swaps.
 */
export function useScrollDirection(
  el: HTMLElement | null,
  threshold = 24,
): 'up' | 'down' {
  const [dir, setDir] = useState<'up' | 'down'>('up')

  useEffect(() => {
    const node = el
    if (!node) return
    let lastY = node.scrollTop
    let acc = 0
    const onScroll = () => {
      const y = node.scrollTop
      const delta = y - lastY
      lastY = y
      if (y <= 0) {
        acc = 0
        setDir('up')
        return
      }
      // Reset the accumulator whenever travel reverses direction.
      if ((delta > 0 && acc < 0) || (delta < 0 && acc > 0)) acc = 0
      acc += delta
      if (acc > threshold) {
        setDir('down')
        acc = 0
      } else if (acc < -4) {
        setDir('up')
        acc = 0
      }
    }
    node.addEventListener('scroll', onScroll, { passive: true })
    return () => node.removeEventListener('scroll', onScroll)
  }, [el, threshold])

  return dir
}
